import React, { useState } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import AddEditFormCst from "@components/AddEditFormCst";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useModuleActivityAddEdit } from "../hooks/useModuleActivityAdd";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";

const ModuleAdd: React.FC = () => {
  const { t } = useTranslation(["manage-project-modules-page"]);
  const { id, IdPlan, IdProject } = useParams();
  const navigate = useNavigate();

  const { columns } = useFetchDetail(
    undefined,
    undefined,
    PagesID["corporate.foundedTraining.managePlan.project.module.edit"],
  );

  const {
    activityAssociation,
    courseAssociation,
    pathAssociation,
    editionAssociation,
    openAssociations,
    showError,
    setOpenAssociation,
    handleAssociation,
    handleDelete,
    handleClearAssociation,
    inputsException,
    getValues,
  } = useModuleActivityAddEdit();

  const [inputFormValidation, setInputFromValidation] = useState();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
    description?: string;
    customContent?: JSX.Element;
  }>();

  const [openToastConfirm, setOpenToastConfirm] = useState<{
    s: StatusEnum;
    message?: string;
    description?: string;
  }>();

  const { trigger: triggerAddModule } = useSWRMutation(
    `${ServicesURL.addModule
      .replace(":corporateId", id || "")
      .replace(":planId", IdPlan || "")
      .replace(":projectId", IdProject || "")}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg });
    },
  );

  const onSubmit = async (inputForm: any) => {
    let loParametr = {};

    if (pathAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          paths: pathAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (courseAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          courses: courseAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (activityAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          activities: activityAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    if (editionAssociation.length > 0) {
      loParametr = {
        ...loParametr,
        ...{
          editions: editionAssociation.map((el: { id: any }) => el.id),
        },
      };
    }
    const objToSend: any = {
      ...inputForm,
      duration: Number(inputForm.duration),
      mandatory_learning_test:
        inputForm.mandatory_learning_test === "true" ? true : false,
      satisfaction_survey:
        inputForm.satisfaction_survey === "true" ? true : false,
      lo_ids: loParametr,
    };

    const { activities, courses, editions, paths, ...resultToSend } = objToSend;

    setOpenToastConfirm(undefined);
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      await triggerAddModule(resultToSend);
      setOpenToast({
        s: StatusEnum.OK,
        message: "",
      });
    } catch (error) {
      setOpenToast(undefined);
    }
  };

  const onValidation = async (inputForm: any) => {
    if (showError) {
      setOpenToast({
        s: StatusEnum.KO,
        message: t("modal-message.error-association") || "",
      });
    } else if (
      activityAssociation.length > 0 ||
      pathAssociation.length > 0 ||
      courseAssociation.length > 0 ||
      editionAssociation.length > 0
    ) {
      setOpenToastConfirm({
        s: StatusEnum.OK,
        message: t("modal-message.attention") || "",
        description: t("modal-message.confirm-association") || "",
      });

      setInputFromValidation(inputForm);
    } else {
      onSubmit(inputForm);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-badges.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("add.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <AddEditFormCst
              columns={columns}
              onSubmit={onValidation}
              actionsForForms={[]}
              getValues={(accessorKey) => getValues(accessorKey)}
              handleAssociation={handleAssociation}
              handleDelete={handleDelete}
              association={openAssociations}
              setOpenAssociation={setOpenAssociation}
              inputsException={inputsException}
              backButton={() => {
                navigate(-1);
              }}
              onReset={handleClearAssociation}
              durationKey={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openToastConfirm}
        title={openToastConfirm?.message || ""}
        description={openToastConfirm?.description || ""}
        onCallBackClosed={() => {
          setOpenToastConfirm(undefined);
        }}
        onActionConfirmed={() => {
          onSubmit(inputFormValidation);
        }}
      />
    </AnimationFadeIn>
  );
};

export default ModuleAdd;
